import { DECISION_MASK, DECISION_STATUS, decisionIsMoney, decisionTypeLabel } from './decision';
import { DECISION_TYPES } from './fixtures';
import type { DecisionType, HrDecision } from './types';

// Mint-HRDecision · trang chi tiết (code board khối CHK / APL / DOCT / TDD / ACTL). Không lương: loại có tiền che số, giữ nguyên chữ kiểm.

export type CheckLevel = 'ok' | 'warn' | 'err';
export const DECISION_CHECKS: Record<DecisionType, ReadonlyArray<readonly [CheckLevel, string]>> = {
  TangLuong: [['ok', 'Bậc L03 trong khung CSD-BKG (L01–L04)'], ['ok', 'Lương mới trong khung bậc L03'], ['warn', 'Căn cứ BHXH chưa đổi theo — đề nghị thêm vào quyết định'], ['ok', 'Đủ 7/7 trường mẫu QD-TANGLUONG']],
  ThangCap: [['ok', 'L04 trong khung CSD-BKG (L01–L04)'], ['warn', 'Lương hiện tại dưới khung L04 — kèm quyết định điều chỉnh lương?'], ['ok', 'Đủ trường mẫu QD-THANGCAP']],
  DieuChuyen: [['warn', 'TRD-DSP định biên 1 đã có THANHTTK → vượt định biên, cần BOD đồng ý'], ['ok', 'Bậc L03 trong khung TRD-DSP (L02–L04)'], ['warn', 'Còn 12 việc hiện trường đang giữ → bàn giao trước ngày hiệu lực']],
  KiemNhiem: [['ok', 'Ghế ACC-CA đang trống · không chồng người giữ'], ['warn', 'Ghế ACC-CA có PAYMENT-APPROVE: chứng từ do NhungTT lập (ACC-TAX) phải leo lên [BOD-DIR]'], ['ok', 'Đủ trường mẫu QD-KIEMNHIEM']],
  ThuMoi: [['ok', 'Lương offer trong khung CSD-CSO × L02'], ['ok', 'Căn cứ BHXH ≥ lương tối thiểu vùng I'], ['ok', 'Đủ trường mẫu TB-THUMOI']],
  BoNhiem: [['ok', 'Ghế BOD-DDIR đang trống trước bổ nhiệm'], ['ok', 'Bậc L07 đủ điều kiện ghế L08 theo diện quyền (Acting)'], ['ok', 'Đủ trường mẫu QD-BONHIEM']],
  NghiViec: [['warn', 'Còn 3 chuyến TRF chưa Finish → bàn giao cho THANHTTK'], ['ok', 'Tạm ứng đã quyết toán'], ['err', 'Chưa có mẫu văn bản nghỉ việc — in bằng mẫu quyết định chung']],
  DieuChinhKPI: [['ok', 'Chỉ số KPI có trong danh mục'], ['warn', 'Chỉ tiêu quý chưa chốt ở họp BOD']],
};

const APPLY: Record<DecisionType, ReadonlyArray<readonly [string, string]>> = {
  TangLuong: [['Thay đổi lương', DECISION_MASK]],
  ThangCap: [['Bậc', 'Executive → Junior'], ['Ghế', 'giữ CSD-BKG, bậc L04']],
  DieuChuyen: [['Phòng / vị trí', 'OPS → TRD · Nhân viên Điều độ'], ['Ghế', 'đóng OPS-FLD 31/Oct · mở TRD-DSP 01/Nov'], ['Backup', 'đổi theo ghế mới']],
  KiemNhiem: [['Ghế', '+ ACC-CA · Acting · 24/Oct–31/Dec'], ['Bước duyệt KTT', '[BOD-DIR] PHILIP → [ACC-CA · Acting] NhungTT']],
  ThuMoi: [['Thay đổi lương', DECISION_MASK]],
  BoNhiem: [['Ghế', '+ BOD-DDIR · Acting'], ['Thẩm quyền', 'theo ghế BOD-DDIR']],
  NghiViec: [['Trạng thái', 'true → false · 31/Oct'], ['Microsoft 365 + HANA Ezy', 'khoá 31/Oct/2026 17:30'], ['OneDrive', 'bàn giao → PHONGNT']],
  DieuChinhKPI: [['Thay đổi lương', DECISION_MASK]],
};
const APPLY_HRD: ReadonlyArray<readonly [string, string]> = [['Ghế', '+ HRD-MGR · Concurrent'], ['KPI', 'chia 60 / 40 theo ghế']];

const DOC_TITLE: Record<DecisionType, string> = {
  TangLuong: 'QUYẾT ĐỊNH · V/v điều chỉnh lương', ThangCap: 'QUYẾT ĐỊNH · V/v thăng cấp', DieuChuyen: 'QUYẾT ĐỊNH · V/v điều chuyển công tác',
  KiemNhiem: 'QUYẾT ĐỊNH · V/v giao kiêm nhiệm', ThuMoi: 'THƯ MỜI NHẬN VIỆC', BoNhiem: 'QUYẾT ĐỊNH · V/v bổ nhiệm',
  NghiViec: 'QUYẾT ĐỊNH · V/v chấm dứt hợp đồng lao động', DieuChinhKPI: 'QUYẾT ĐỊNH · V/v điều chỉnh KPI',
};

const tail = (u: string) => u.replace(/^\[[^\]]*\]\s*/, '');
const head = (u: string) => u.match(/^\[[^\]]*\]/)?.[0] ?? '';
const dayOf = (x: string) => x.slice(0, 6);

export interface DecisionTodo { id: string; k: string; who: string; due: string; tone: 'warn' | 'err'; act?: string }
export interface DecisionStep { seat: string; who: string; st: 'done' | 'cur' | 'rej' | 'todo'; stK: string; sub: string }

export interface DecisionView {
  doc: { title: string; file: string; sub: string };
  before: string; after: string; masked: boolean;
  checks: Array<{ lv: CheckLevel; k: string }>;
  applyK: string; apply: Array<{ k: string; v: string }>; seatLink: boolean;
  todos: DecisionTodo[]; steps: DecisionStep[];
  info: Array<{ k: string; v: string; tone?: 'warn' | 'ok' | 'err'; copy?: boolean; href?: boolean }>;
  people: Array<{ u: string; role: string; hei: boolean }>;
  acts: Array<{ k: string; who: string; when: string; kind: string }>;
  primary: { label: string; kind: 'approve' | 'send' | 'redo' | 'welcome' | 'print' } | null;
}

export function decisionView(d: HrDecision): DecisionView {
  const tpl = DECISION_TYPES.find((x) => x.type === d.type)?.template ?? '';
  const masked = decisionIsMoney(d.type);
  const creator = tail(d.approver);
  const dir = 'PHILIP', hr = 'HANHLTM';
  const dirSeat = '[BOD-DIR]';
  const lab = decisionTypeLabel(d.type);
  const checks = DECISION_CHECKS[d.type].map(([lv, k]) => ({ lv, k }));
  const apply = (d.type === 'KiemNhiem' && d.user !== 'NhungTT' ? APPLY_HRD : APPLY[d.type]).map(([k, v]) => ({ k, v }));
  const todos: DecisionTodo[] = [];
  if (d.status === 'Pending') todos.push({ id: 'ap', k: `Duyệt ${lab.toLowerCase()} · ${d.user}`, who: dir, due: `đang chờ · từ ${dayOf(d.createdAt)}`, tone: 'warn', act: 'Duyệt' });
  if (d.status === 'Draft') todos.push({ id: 'snd', k: 'Gửi duyệt bản nháp', who: hr, due: 'chưa gửi', tone: 'warn', act: 'Gửi duyệt' });
  if (d.status === 'Rejected') todos.push({ id: 'redo', k: 'Lập lại theo lý do từ chối', who: hr, due: 'sau khi sửa', tone: 'err', act: 'Lập lại' });
  checks.forEach((c, i) => { if (c.lv !== 'ok') todos.push({ id: `ck${i}`, k: c.k, who: hr, due: `trước ${d.effective}`, tone: c.lv === 'err' ? 'err' : 'warn' }); });
  if (d.type === 'ThuMoi' && d.status === 'Approved') todos.push({ id: 'wel', k: 'Gửi thư chào mừng', who: hr, due: `trước ${d.effective}`, tone: 'warn', act: 'Gửi' });

  const dirSt: DecisionStep['st'] = d.status === 'Approved' ? 'done' : d.status === 'Rejected' ? 'rej' : d.status === 'Draft' ? 'todo' : 'cur';
  const steps: DecisionStep[] = [
    { seat: head(d.approver) || '[HRD-MGR · kiêm]', who: creator, st: 'done', stK: 'Đã lập', sub: `Lập · ${d.createdAt}` },
    { seat: dirSeat, who: dir, st: dirSt, stK: DECISION_STATUS[d.status].label === 'Chờ duyệt' ? 'Đang chờ' : dirSt === 'done' ? 'Đã duyệt' : dirSt === 'rej' ? 'Đã từ chối' : 'Chưa gửi',
      sub: d.status === 'Pending' ? `Đang chờ · từ ${dayOf(d.createdAt)}` : d.status === 'Draft' ? 'Chưa gửi' : 'Theo ghế' },
  ];

  const st = DECISION_STATUS[d.status];
  const acts: DecisionView['acts'] = [{ k: `Lập quyết định · mẫu ${tpl}`, who: hr, when: d.createdAt, kind: 'Lập' }];
  if (d.status !== 'Draft') acts.unshift({ k: `Gửi duyệt → ${dirSeat} ${dir}`, who: hr, when: d.createdAt, kind: 'Gửi' });
  if (d.status === 'Approved') acts.unshift({ k: `Đã duyệt · lịch áp vào hồ sơ ${d.effective}`, who: dir, when: `${dayOf(d.createdAt)} · sau gửi`, kind: 'Duyệt' });
  if (d.status === 'Rejected') acts.unshift({ k: `Từ chối: ${d.reason ?? '—'}`, who: dir, when: `${dayOf(d.createdAt)} · sau gửi`, kind: 'Từ chối' });

  const primary: DecisionView['primary'] = d.status === 'Pending' ? { label: 'Duyệt', kind: 'approve' }
    : d.status === 'Draft' ? { label: 'Gửi duyệt', kind: 'send' }
    : d.status === 'Rejected' ? { label: 'Lập lại từ quyết định này', kind: 'redo' }
    : d.type === 'ThuMoi' ? { label: 'Gửi thư chào mừng', kind: 'welcome' } : null;

  return {
    doc: { title: DOC_TITLE[d.type], file: `${d.no}.pdf`, sub: `${d.no}.pdf · mẫu ${tpl} · GIÁM ĐỐC · ${dirSeat} ${dir}` },
    before: masked ? DECISION_MASK : d.before, after: masked ? DECISION_MASK : d.after, masked,
    checks, applyK: d.status === 'Approved' ? `Đã áp vào hồ sơ${d.type === 'ThuMoi' ? ' (thư mời không ghi ngược hồ sơ)' : ''}` : 'Khi duyệt, hệ thống tự áp vào hồ sơ',
    apply, seatLink: d.type === 'KiemNhiem' || d.type === 'BoNhiem', todos, steps,
    info: [
      { k: 'Số QĐ', v: d.no, copy: true }, { k: 'Mẫu', v: tpl }, { k: 'Nhân sự', v: d.name, href: true },
      { k: 'Trạng thái', v: `${st.label} · ${d.status}`, ...(st.tone === 'mute' ? {} : { tone: st.tone === 'err' ? 'err' as const : st.tone }) }, { k: 'Lập lúc', v: d.createdAt },
    ],
    people: [
      { u: d.user, role: `Người được quyết định · ${d.seat}`, hei: d.type !== 'ThuMoi' },
      { u: creator, role: `${head(d.approver)} lập quyết định`.trim(), hei: true },
      { u: dir, role: `${dirSeat} duyệt · ký văn bản`, hei: true },
    ],
    acts, primary,
  };
}
